import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { z } from 'zod'
import { Button, TextField } from '@shared/design-system/ui'
import type { AttachBankAccountInput } from '../api/verification'
import { brazilianBanks } from '../lib/brazilian-banks'
import {
  actionRow,
  form,
  formFields,
  fullWidth,
  helper,
  subheading,
  bankPicker,
  bankOptions,
  bankOption,
  bankOptionActive,
  bankCode,
} from './bank-account-section.css'

const bankAccountInputSchema = z.object({
  bankCode: z.string().regex(/^\d{3}$/, 'Informe o código do banco com 3 dígitos.'),
  branch: z.string().regex(/^\d{4}$/, 'Informe a agência com 4 dígitos.'),
  accountNumber: z
    .string()
    // Keeps digits plus an "X" check digit (used by e.g. Banco do Brasil); drops hyphens and spaces.
    .transform((value) => value.toUpperCase().replace(/[^\dX]/g, ''))
    .pipe(
      z
        .string()
        .min(2, 'Informe a conta com pelo menos 2 dígitos.')
        .max(20, 'A conta deve ter no máximo 20 dígitos.')
        .regex(/^\d+X?$/, 'Use apenas números; somente o dígito verificador pode ser X.'),
    ),
})

type BankAccountFormField = keyof AttachBankAccountInput
type BankAccountFormValues = Record<BankAccountFormField, string>
type BankAccountFormErrors = Partial<Record<BankAccountFormField, string>>
type BankPickerOption =
  | { kind: 'bank'; code: string; name: string }
  | { kind: 'other'; code: ''; name: string }

const initialFormValues: BankAccountFormValues = { bankCode: '', branch: '', accountNumber: '' }

/** Validates and submits a new bank account; `onSubmit` resolves `true` when the account was saved. */
export function BankAccountForm({
  replacing,
  disabled,
  onChange,
  onSubmit,
  serverErrors,
  error,
  registrationFeedbackEnabled = false,
}: {
  replacing: boolean
  disabled: boolean
  onChange: () => void
  onSubmit: (input: AttachBankAccountInput) => Promise<boolean>
  serverErrors?: BankAccountFormErrors
  /** Submit-level error, shown right above the submit button. */
  error?: ReactNode
  registrationFeedbackEnabled?: boolean
}) {
  const [formValues, setFormValues] = useState(initialFormValues)
  const [formErrors, setFormErrors] = useState<BankAccountFormErrors>({})
  const [bankSearch, setBankSearch] = useState('')
  const [bankMenuOpen, setBankMenuOpen] = useState(false)
  const [activeBankIndex, setActiveBankIndex] = useState(0)
  const [otherBank, setOtherBank] = useState(false)
  const bankPickerRef = useRef<HTMLDivElement>(null)
  const bankInputRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()

  const filteredBanks = useMemo(() => {
    const search = bankSearch.trim().toLocaleLowerCase('pt-BR')
    if (!search) return brazilianBanks
    return brazilianBanks.filter(
      (bank) => bank.code.includes(search) || bank.name.toLocaleLowerCase('pt-BR').includes(search),
    )
  }, [bankSearch])

  const bankPickerOptions = useMemo<BankPickerOption[]>(
    () => [
      ...filteredBanks.map((bank) => ({ kind: 'bank' as const, ...bank })),
      { kind: 'other', code: '', name: 'Outro banco (informar código)' },
    ],
    [filteredBanks],
  )

  useEffect(() => {
    if (!bankMenuOpen) return
    function handleOutsidePointerDown(event: PointerEvent) {
      if (!bankPickerRef.current?.contains(event.target as Node)) {
        setBankMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', handleOutsidePointerDown)
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown)
  }, [bankMenuOpen])

  function updateField(field: BankAccountFormField, value: string) {
    setFormValues((current) => ({ ...current, [field]: value }))
    setFormErrors((current) => ({ ...current, [field]: undefined }))
    onChange()
  }

  function selectBank(code: string, name: string) {
    setOtherBank(false)
    setBankSearch(`${code} · ${name}`)
    setBankMenuOpen(false)
    updateField('bankCode', code)
  }

  function selectOtherBank() {
    setOtherBank(true)
    setBankSearch('Outro banco')
    setBankMenuOpen(false)
    updateField('bankCode', '')
  }

  function optionId(index: number): string {
    return `${listboxId}-option-${index}`
  }

  function selectActiveBank() {
    const option = bankPickerOptions[activeBankIndex]
    if (!option) return
    if (option.kind === 'other') {
      selectOtherBank()
    } else {
      selectBank(option.code, option.name)
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const parsed = bankAccountInputSchema.safeParse(formValues)
    if (!parsed.success) {
      setFormErrors(fieldErrors(parsed.error))
      return
    }
    setFormErrors({})
    if (await onSubmit(parsed.data)) {
      setFormValues(initialFormValues)
      setBankSearch('')
      setOtherBank(false)
    }
  }

  return (
    <form
      className={form}
      onSubmit={(event) => void submit(event)}
      noValidate
      data-testid="onboarding-bank-form"
    >
      <h3 className={subheading}>{replacing ? 'Trocar conta' : 'Cadastrar conta'}</h3>
      <p className={helper}>
        A conta deve estar no nome do titular do cadastro, com o nome do vendedor para PF ou a razão
        social da empresa para PJ.
      </p>
      {replacing ? (
        <p className={helper}>
          {registrationFeedbackEnabled
            ? 'A conta atual permanece cadastrada até que a nova seja confirmada.'
            : 'A conta atual continua recebendo repasses até que a nova seja confirmada.'}
        </p>
      ) : null}
      <div className={formFields}>
        <div ref={bankPickerRef} className={fullWidth + ' ' + bankPicker}>
          <TextField
            ref={bankInputRef}
            label="Banco"
            placeholder="Busque por código ou nome"
            autoComplete="off"
            role="combobox"
            aria-expanded={bankMenuOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={bankMenuOpen ? optionId(activeBankIndex) : undefined}
            value={bankSearch}
            error={!otherBank ? (serverErrors?.bankCode ?? formErrors.bankCode) : undefined}
            onFocus={() => {
              setBankMenuOpen(true)
              setActiveBankIndex(0)
            }}
            onBlur={() => {
              window.setTimeout(() => {
                if (!bankPickerRef.current?.contains(document.activeElement)) {
                  setBankMenuOpen(false)
                }
              }, 0)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setBankMenuOpen(true)
                setActiveBankIndex((index) =>
                  Math.min(index + (bankMenuOpen ? 1 : 0), bankPickerOptions.length - 1),
                )
              } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                setBankMenuOpen(true)
                setActiveBankIndex((index) => Math.max(index - (bankMenuOpen ? 1 : 0), 0))
              } else if (event.key === 'Enter' && bankMenuOpen) {
                event.preventDefault()
                selectActiveBank()
              } else if (event.key === 'Escape') {
                event.preventDefault()
                setBankMenuOpen(false)
              }
            }}
            onChange={(event) => {
              setOtherBank(false)
              setBankSearch(event.target.value)
              setBankMenuOpen(true)
              setActiveBankIndex(0)
              updateField('bankCode', '')
            }}
          />
          {bankMenuOpen ? (
            <ul id={listboxId} className={bankOptions} role="listbox" aria-label="Bancos">
              {bankPickerOptions.map((option, index) => (
                <li
                  id={optionId(index)}
                  key={option.kind === 'other' ? 'other' : option.code}
                  role="option"
                  aria-selected={
                    option.kind === 'other' ? otherBank : formValues.bankCode === option.code
                  }
                >
                  <button
                    type="button"
                    className={`${bankOption} ${index === activeBankIndex ? bankOptionActive : ''}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() =>
                      option.kind === 'other'
                        ? selectOtherBank()
                        : selectBank(option.code, option.name)
                    }
                  >
                    <span>{option.name}</span>
                    {option.kind === 'bank' ? (
                      <span className={bankCode}>{option.code}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {otherBank ? (
          <TextField
            className={fullWidth}
            label="Código do banco"
            helperText="3 dígitos"
            inputMode="numeric"
            autoComplete="off"
            maxLength={3}
            value={formValues.bankCode}
            error={serverErrors?.bankCode ?? formErrors.bankCode}
            onChange={(event) => updateField('bankCode', event.target.value)}
          />
        ) : null}
        <TextField
          label="Agência, sem dígito"
          helperText="4 dígitos"
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          value={formValues.branch}
          error={serverErrors?.branch ?? formErrors.branch}
          onChange={(event) => updateField('branch', event.target.value)}
        />
        <TextField
          label="Conta com dígito"
          helperText="Informe também o dígito verificador."
          inputMode="numeric"
          autoComplete="off"
          maxLength={40}
          value={formValues.accountNumber}
          error={serverErrors?.accountNumber ?? formErrors.accountNumber}
          onChange={(event) => updateField('accountNumber', event.target.value)}
        />
      </div>
      {error}
      <div className={actionRow}>
        <Button type="submit" disabled={disabled}>
          {replacing ? 'Trocar conta' : 'Cadastrar conta'}
        </Button>
      </div>
    </form>
  )
}

function fieldErrors(error: z.ZodError): BankAccountFormErrors {
  const errors: BankAccountFormErrors = {}
  for (const issue of error.issues) {
    const field = issue.path[0]
    if (
      (field === 'bankCode' || field === 'branch' || field === 'accountNumber') &&
      errors[field] === undefined
    ) {
      errors[field] = issue.message
    }
  }
  return errors
}
